<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
              >
                Search Form
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-form @submit.prevent="submit">
                  <v-row class="px-2 pt-4 pb-0">
                    <v-col lg="2" md="2" cols="12">
                      <v-date-input
                        label="From Date"
                        density="compact"
                        prepend-icon=""
                        prepend-inner-icon="mdi-calendar-month"
                        variant="outlined"
                        bg-color="grey-lighten-4"
                        v-model="from_date.value.value"
                        :error-messages="from_date.errorMessage.value"
                        autocomplete="off"
                        clearable
                      ></v-date-input>
                    </v-col>

                    <v-col lg="2" md="2" cols="12">
                      <v-date-input
                        label="To Date"
                        density="compact"
                        prepend-icon=""
                        prepend-inner-icon="mdi-calendar-month"
                        variant="outlined"
                        bg-color="grey-lighten-4"
                        v-model="to_date.value.value"
                        :error-messages="to_date.errorMessage.value"
                        autocomplete="off"
                        clearable
                      ></v-date-input>
                    </v-col>

                    <v-col lg="2" md="2" cols="12">
                      <v-btn
                        class="bg-brand w-15"
                        type="submit"
                        ref="searchButton"
                      >
                        Search
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>

    <v-container fluid style="margin-top: -20px">
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
              >
                Production Order Records
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-row class="py-2">
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="search"
                      append-inner-icon="mdi-magnify"
                      label="Search"
                      single-line
                      hide-details
                      density="compact"
                      variant="outlined"
                      bg-color="grey-lighten-4"
                      class="pl-2"
                      clearable
                    ></v-text-field>
                  </v-col>

                  <v-col md="5" cols="12">
                    <div class="d-flex align-center flex-column">
                      <v-btn-group
                        variant="outlined"
                        density="compact"
                        size="x-small"
                        divided
                      >
                        <v-btn
                          prepend-icon="mdi-download"
                          variant="flat"
                          color="success"
                          @click="exportToCSV"
                        >
                          CSV
                        </v-btn>
                        <v-btn
                          prepend-icon="mdi-download"
                          variant="flat"
                          color="primary"
                          @click="exportToExcel"
                        >
                          EXCEL
                        </v-btn>
                        <v-btn
                          prepend-icon="mdi-download"
                          variant="flat"
                          color="red"
                          @click="exportToPDF"
                        >
                          PDF
                        </v-btn>
                      </v-btn-group>
                    </div>
                  </v-col>

                  <v-col md="3" cols="12">
                    <div class="text-right">
                      <v-btn
                        v-if="$insertAccess($route.fullPath)"
                        @click="goToCreate"
                        color="primary"
                        variant="tonal"
                        prepend-icon="mdi-plus"
                        rounded="xl"
                      >
                        Add Production Order
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>

                <v-row class="pt-0">
                  <v-col cols="12">
                    <div class="scrollable-table-wrapper">
                      <v-data-table
                        :headers="headers"
                        :items="productionOrderList"
                        :loading="loading"
                        v-model:items-per-page="itemsPerPage"
                        :search="search"
                        :hover="true"
                        density="compact"
                        class="responsive-table"
                      >
                        <template v-slot:item.index="{ index }">
                          {{ ++index }}
                        </template>

                        <template v-slot:item.code="{ item }">
                          {{ item.code }}
                        </template>

                        <template v-slot:item.buyer_order="{ item }">
                          {{ item.buyer_order?.code || "-" }}
                        </template>

                        <template v-slot:item.factory="{ item }">
                          {{ item.factory?.supplier_name || "-" }}
                        </template>

                        <template v-slot:item.allocation_date="{ item }">
                          {{ formatDate(item.allocation_date) }}
                        </template>

                        <template v-slot:item.ex_factory_date="{ item }">
                          {{ item.ex_factory_date ? formatDate(item.ex_factory_date) : "-" }}
                        </template>

                        <template v-slot:item.shipment_date="{ item }">
                          {{ item.shipment_date ? formatDate(item.shipment_date) : "-" }}
                        </template>

                        <template v-slot:item.total_items="{ item }">
                          {{ item.items ? item.items.length : 0 }}
                        </template>

                        <template v-slot:item.status="{ item }">
                          <v-chip
                            size="small"
                            :color="item.status == 1 ? 'success' : 'red'"
                          >
                            {{ item.status == 1 ? "Active" : "Inactive" }}
                          </v-chip>
                        </template>

                        <template v-slot:item.actions="{ item }">
                          <div class="d-flex align-center justify-center" style="gap: 6px;">
                            <v-tooltip text="Edit">
                              <template v-slot:activator="{ props }">
                                <v-btn
                                  v-bind="props"
                                  icon="mdi-pencil"
                                  size="small"
                                  variant="text"
                                  color="primary"
                                  @click="goToEdit(item.id)"
                                ></v-btn>
                              </template>
                            </v-tooltip>

                            <v-tooltip text="View">
                              <template v-slot:activator="{ props }">
                                <v-btn
                                  v-bind="props"
                                  icon="mdi-eye"
                                  size="small"
                                  variant="text"
                                  color="info"
                                  @click="viewDetails(item)"
                                ></v-btn>
                              </template>
                            </v-tooltip>
                          </div>
                        </template>
                      </v-data-table>
                    </div>
                  </v-col>
                </v-row>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>

    <!-- Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="900px" persistent>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>Production Order Details</span>
          <v-btn icon="mdi-close" variant="text" @click="detailsDialog = false"></v-btn>
        </v-card-title>
        <v-card-text v-if="selectedItem">
          <v-row>
            <v-col cols="12" md="6">
              <strong>Code:</strong> {{ selectedItem.code }}
            </v-col>
            <v-col cols="12" md="6">
              <strong>Buyer Order:</strong> {{ selectedItem.buyer?.company_name || "-" }}
            </v-col>
            <v-col cols="12" md="6">
              <strong>Factory:</strong> {{ selectedItem.factory?.supplier_name || "-" }}
            </v-col>
            <v-col cols="12" md="6">
              <strong>Allocation Date:</strong> {{ formatDate(selectedItem.allocation_date) }}
            </v-col>
            <v-col cols="12" md="6">
              <strong>Ex Factory Date:</strong> {{ selectedItem.ex_factory_date ? formatDate(selectedItem.ex_factory_date) : "-" }}
            </v-col>
            <v-col cols="12" md="6">
              <strong>Shipment Date:</strong> {{ selectedItem.shipment_date ? formatDate(selectedItem.shipment_date) : "-" }}
            </v-col>
            <v-col cols="12">
              <strong>Items:</strong>
              <v-table v-if="selectedItem.items && selectedItem.items.length > 0">
                <thead>
                  <tr>
                    <th>Style</th>
                    <th>Order Quantity</th>
                    <th>Unit Price</th>
                    <th>Total Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, idx) in selectedItem.items" :key="idx">
                    <td>{{ item.style?.name || "-" }}</td>
                    <td>{{ item.order_quantity }}</td>
                    <td>{{ item.unit_price }}</td>
                    <td>{{ item.total_value }}</td>
                  </tr>
                </tbody>
              </v-table>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="detailsDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import { ref, onMounted } from "vue";
import { useForm, useField } from "vee-validate";
import * as yup from "yup";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { toast } from "vue3-toastify";

export default {
  name: "ProductionOrderList",

  data() {
    return {
      panel: [0],
      loading: false,
      search: "",
      itemsPerPage: 10,
      detailsDialog: false,
      selectedItem: null,
      headers: [
        {
          title: "#SL",
          align: "center",
          key: "index",
          width: "80px",
          sortable: false,
        },
        {
          title: "Code",
          key: "code",
          align: "left",
          sortable: true,
        },
        {
          title: "Buyer Order",
          key: "buyer_order",
          align: "left",
          sortable: false,
        },
        {
          title: "Factory",
          key: "factory",
          align: "left",
          sortable: false,
        },
        {
          title: "Allocation Date",
          key: "allocation_date",
          align: "left",
          sortable: true,
        },
        {
          title: "Ex Factory Date",
          key: "ex_factory_date",
          align: "left",
          sortable: true,
        },
        {
          title: "Shipment Date",
          key: "shipment_date",
          align: "left",
          sortable: true,
        },
        {
          title: "Items",
          key: "total_items",
          align: "center",
          sortable: false,
        },
        {
          title: "Status",
          key: "status",
          align: "center",
          sortable: true,
        },
        {
          title: "Actions",
          key: "actions",
          align: "center",
          sortable: false,
          width: "120px",
        },
      ],
    };
  },

  computed: {
    productionOrderList() {
      return this.$store.getters.getProductionOrderList;
    },
  },

  setup() {
    const store = useStore();
    const router = useRouter();

    const { handleSubmit } = useForm({
      validationSchema: yup.object({
        from_date: yup.date().nullable(),
        to_date: yup.date().nullable(),
      }),
    });

    const from_date = useField("from_date");
    const to_date = useField("to_date");

    const submit = handleSubmit((values) => {
      // Implement search logic if needed
      store.dispatch("getProductionOrderList");
    });

    const goToCreate = () => {
      router.push({ name: "production-order-create" });
    };

    const goToEdit = (id) => {
      router.push({ name: "production-order-create", params: { id } });
    };

    const formatDate = (date) => {
      if (!date) return "-";
      const d = new Date(date);
      return d.toLocaleDateString();
    };

    const viewDetails = (item) => {
      // Load full details if needed

      router.push({ name: "production-order-view", params: { id: item.id } });

      // store
      //   .dispatch("getProductionOrderById", { id: item.id })
      //   .then((res) => {
      //     this.selectedItem = res.data.data;
      //     this.detailsDialog = true;
      //   })
      //   .catch(() => {
      //     toast.error("Failed to load production order details");
      //   });
    };

    const exportToCSV = () => {
      toast.info("CSV export functionality to be implemented");
    };

    const exportToExcel = () => {
      toast.info("Excel export functionality to be implemented");
    };

    const exportToPDF = () => {
      toast.info("PDF export functionality to be implemented");
    };

    onMounted(() => {
      store.dispatch("getProductionOrderList");
    });

    return {
      from_date,
      to_date,
      submit,
      goToCreate,
      goToEdit,
      formatDate,
      viewDetails,
      exportToCSV,
      exportToExcel,
      exportToPDF,
    };
  },

  methods: {
    loadList() {
      this.loading = true;
      this.$store.dispatch("getProductionOrderList").finally(() => {
        this.loading = false;
      });
    },
  },

  created() {
    this.loadList();
  },
};
</script>

<style scoped>
.scrollable-table-wrapper {
  overflow-x: auto;
}

.responsive-table {
  width: 100%;
}
</style>

